import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import { IBoondock, IBoondockWhere, IPhoto } from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'boondock';

export interface IAdminBoondockPage {
  total: number;
  page: number;
  boondocks: IBoondock[];
}

type ActionResult = { success: boolean; message: string };

/**
 * Admin boondock CRUD — replaces @curbnturf/boondock-listing's BoondockService
 * for admin. cnt-landing's BoondockApiService is read/favorite only.
 */
@Injectable({ providedIn: 'root' })
export class AdminBoondockApiService {
  private readonly http = inject(HttpClient);

  list(where: IBoondockWhere, page: number, pageSize: number, sort = 'id'): Observable<IAdminBoondockPage> {
    const query = objectToUrlParams({ ...where, sort, page, pageSize });
    return this.http.get<IAdminBoondockPage>(`${API_URL}?${query}`);
  }

  fetch(boondockId: number): Observable<IBoondock> {
    return this.http.get<IBoondock>(`${API_URL}/${boondockId}`);
  }

  create(boondock: IBoondock): Observable<IBoondock> {
    return this.http.post<IBoondock>(API_URL, boondock);
  }

  /** `PUT /boondock/:id` takes the whole boondock (server ignores the path id). */
  update(boondock: IBoondock): Observable<IBoondock> {
    if (!boondock.id) {
      throw new Error('Missing Boondock Identifier');
    }
    return this.http.put<IBoondock>(`${API_URL}/${boondock.id}`, boondock);
  }

  remove(boondockId: number): Observable<IBoondock> {
    return this.http.delete<IBoondock>(`${API_URL}/${boondockId}`);
  }

  restore(boondockId: number): Observable<IBoondock> {
    return this.http.post<IBoondock>(`${API_URL}/${boondockId}/restore`, null);
  }

  enable(boondockId: number): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${API_URL}/${boondockId}/enable`, null);
  }

  disable(boondockId: number): Observable<ActionResult> {
    return this.http.post<ActionResult>(`${API_URL}/${boondockId}/disable`, null);
  }

  uploadImage(boondockId: number, formData: FormData): Observable<{ id: number; photos: IPhoto[] }> {
    return this.http.post<{ id: number; photos: IPhoto[] }>(`${API_URL}/${boondockId}/upload-image`, formData);
  }

  removeImage(boondockId: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${boondockId}/remove-image/${photoIndex}`);
  }
}
